import { blankSpans, el, replaceChildrenWith, untimedSpans } from "./dom";
import {
  channelKey,
  channelLabel,
  duration,
  languageName,
  outcomeKey,
  outcomeLabel,
  positionKey,
  positionLabel
} from "./format";
import {
  decimalMinutes,
  firstStartMs,
  sessionsForSignUp,
  spokenSeconds,
  turnStamp
} from "./speakingTurns";
import type { TurnIndex } from "./speakingTurns";
import type { AgendaItem, DataAvailability, SignUp } from "./types";

/**
 * AB#9981. One agenda item's roster: who signed up for this item, grouped by
 * channel, sortable on every column that carries data.
 *
 * Two rules shape this file.
 *
 * **Columns are removed, not dashed.** `availability` decides which columns exist at
 * all. A Language column full of dashes tells the reader the data is broken; no
 * Language column tells them the meeting had no phone callers, which is the truth.
 *
 * **Sort state lives in the accessible name.** `aria-sort` is only valid on a
 * `columnheader`, and these rosters are grids rather than tables, so a screen reader
 * user is told the direction through the button's label instead.
 */

export type RosterColumn =
  | "speaker"
  | "organization"
  | "channel"
  | "position"
  | "outcome"
  | "started"
  | "spokeFor"
  | "length"
  | "allotment"
  | "language";

export interface RosterOptions {
  item: AgendaItem;
  availability: DataAvailability;
  /** Speaking turns placed in time, indexed once per render. Absent on a meeting with no
   *  timeline, in which case the Started / Spoke for columns are not built at all. */
  turns: TurnIndex;
  /** Called with the person's user id when a speaker name is activated. */
  onSpeakerActivated: (userId: string, trigger: HTMLElement) => void;
  /** Rows drawn per item. Beyond this the roster states what it omitted. */
  renderCap: number;
}

interface ColumnSpec {
  key: RosterColumn;
  label: string;
  sortable: boolean;
  /** Sort key. Null sorts LAST in ascending order regardless of direction, because
   * "not timed" is an absence rather than a small value. */
  value: (signUp: SignUp) => string | number | null;
}

/**
 * Channel is BOTH a grouping and a column, and that is not redundancy.
 *
 * Rows are grouped by channel with a band above each group, which is how the prototype
 * read and the reason a reader never has to sort to answer "how many called in". The
 * column exists because once a reader sorts by Length, a row halfway down the page has
 * lost sight of the band it belongs to.
 *
 * The column is NOT sortable, deliberately. draw() sorts globally and then re-partitions
 * into groups, so sorting by channel would appear to do nothing while the bands silently
 * overrode it.
 */
function columnsFor(availability: DataAvailability, turns: TurnIndex): ColumnSpec[] {
  const columns: ColumnSpec[] = [
    { key: "speaker", label: "Speaker", sortable: true, value: (s) => s.speakerName.toLowerCase() }
  ];

  // Organization is its own column rather than a second line under the name. It is the
  // column that shows organised turnout -- which items drew a neighbourhood association
  // rather than individuals -- and that is unreadable when it is buried in another cell.
  if (availability.organizations) {
    columns.push({
      key: "organization",
      label: "Organization represented",
      sortable: true,
      value: (s) => s.organization.toLowerCase()
    });
  }

  // Channel is BOTH a band above each group and a column, and the column is deliberately
  // NOT sortable. draw() sorts globally and then re-partitions into channel groups, so a
  // sortable channel column would appear to do nothing while the bands silently overrode
  // it. The band answers "how many called in"; the column keeps a row readable on its own
  // once some other column has been sorted.
  columns.push({
    key: "channel",
    label: "Channel",
    sortable: false,
    value: (s) => s.channel
  });

  if (availability.positions) {
    columns.push({
      key: "position",
      label: "Position",
      sortable: true,
      value: (s) => positionLabel(s.position).toLowerCase()
    });
  }

  columns.push({
    key: "outcome",
    label: "Outcome",
    sortable: true,
    value: (s) => outcomeLabel(s.outcome).toLowerCase()
  });

  // AB#9981. When the microphone was actually theirs.
  //
  // Requested in as many words by Los Angeles County: "the individual speaker's start and
  // stop talk times and the exact amount of time spoken". Gated on `timeline` -- the flag
  // that already means "some turn or some caller leg carries a usable timestamp" -- and on
  // the index actually holding a turn, because a Started column that reads "Not recorded"
  // the whole way down is the column-of-dashes this file removes columns to avoid.
  //
  // The end time is deliberately NOT a roster column. Start plus length gives it, a roster
  // row is already eight columns wide at 320px, and the profile's Speaking sessions table
  // carries every endpoint per turn -- which is also the only place that can, because a
  // speaker may hold the microphone more than once on one item and a roster row is one row.
  if (availability.timeline && turns.any) {
    columns.push({
      key: "started",
      label: "Started",
      sortable: true,
      value: (s) => firstStartMs(sessionsForSignUp(turns, s))
    });

    // Decimal minutes, not m:ss, and next to Length rather than instead of it. They are the
    // same quantity: Length is the shape a reader SCANS, this is the shape the requesting
    // clerk's own spreadsheet SUMS. Sitting adjacent is what lets a reader see they agree.
    columns.push({
      key: "spokeFor",
      label: "Spoke for (min)",
      sortable: true,
      value: (s) => spokenSeconds(sessionsForSignUp(turns, s)).seconds
    });
  }

  if (availability.speakingTimes) {
    columns.push({
      key: "length",
      label: "Length",
      sortable: true,
      value: (s) => s.speakingSeconds
    });
  }

  // Percent of allotment, not the raw allotment. "3:00 each" used to sit on the item head
  // and told the reader the rule; what a clerk needs is which speakers ran up against it.
  // Null whenever either side is missing, and the existing nulls-last comparator already
  // handles that correctly -- an absence is not a small value.
  if (availability.speakingTimes && availability.allotments) {
    columns.push({
      key: "allotment",
      label: "Of allotment",
      sortable: true,
      value: (s) =>
        s.speakingSeconds === null || s.allottedSeconds === null || s.allottedSeconds === 0
          ? null
          : Math.round((s.speakingSeconds / s.allottedSeconds) * 100)
    });
  }

  if (availability.languages) {
    columns.push({
      key: "language",
      label: "Language",
      sortable: true,
      // By the name the reader sees, so `en` and `en-US` sort together; blank sorts last.
      value: (s) => languageName(s.languageCode).toLowerCase() || null
    });
  }

  return columns;
}

interface SortState {
  column: RosterColumn | null;
  direction: "ascending" | "descending";
}

export function renderRoster(options: RosterOptions): {
  toggle: HTMLButtonElement;
  panel: HTMLElement;
} {
  const { item, availability } = options;
  const columns = columnsFor(availability, options.turns);
  const sort: SortState = { column: null, direction: "ascending" };

  const panelId = `spr-roster-${item.id}`;

  // The label is a COUNT, never a sentence -- including when the count is zero.
  //
  // It used to read "No speakers registered for this item", which made the control as
  // wide as the sentence. On an agenda where most items draw nobody, that turned the
  // page into a wall of full-width buttons and buried the handful of items that did
  // draw speakers. The sentence still exists, once, inside the opened panel below,
  // which is where a reader asking "who spoke on this?" is actually looking.
  //
  // The toggle is kept on empty items rather than dropped: removing it would make an
  // empty item the one row a keyboard user cannot reach, and would leave the
  // aria-controls association below pointing at nothing.
  // The control lives in the ITEM column and IS the item number.
  //
  // It used to be a "› 55 signed up" pill in a 110px column of its own on the right. Two
  // problems with that: the count is already in the two channel columns beside it, and a
  // whole track was spent restating it while the title next door had 280px to hold a
  // sentence. Moving the number in here gives the number a column, gives the title the
  // track back, and puts the disclosure where a reader reaches for it -- at the start of
  // the row, on the thing being disclosed.
  //
  // What is NOT allowed to change: it stays a real <button> with aria-expanded and
  // aria-controls pointing at the panel, on EVERY item including the ones nobody signed
  // up for. Dropping it from an empty item once made that item the single row a keyboard
  // user could not reach.
  const signUpCount = item.signUps.length;
  const numberLabel = item.itemNumber.trim();
  const toggle = el("button", {
    type: "button",
    class: "spr-roster-toggle",
    "data-testid": "speaker-report-roster-toggle",
    "aria-expanded": "false",
    "aria-controls": panelId,
    "data-agenda-item-id": item.id,
    "data-signups": String(signUpCount)
  }, [
    el("span", { class: "spr-chev", "aria-hidden": "true", text: "›" }),
    // aria-hidden because the accessible name below already names the item. Left visible
    // it would be read twice, once as a bare digit.
    ...(numberLabel
      ? [el("span", { class: "spr-item-no", "aria-hidden": "true", text: numberLabel })]
      : []),
    // The accessible name, assembled from content rather than an aria-label so it stays
    // in one place: "2 signed up for item 20". The count leads because the count is what
    // the control is offering to show, and it is still a COUNT and never a sentence --
    // "No speakers registered for this item" once made every empty row a wall of text.
    el("span", {
      class: "spr-roster-toggle-label spr-sr",
      text: `${signUpCount} signed up`
    }),
    el("span", {
      class: "spr-sr",
      text: numberLabel ? ` for item ${numberLabel}` : ` for ${item.title || "this agenda item"}`
    })
  ]);

  // role="grid" is REQUIRED, not decorative. The rows below declare role="row" and the
  // cells role="gridcell"/"columnheader", and every one of those roles must be contained
  // by a grid, table, rowgroup or treegrid. Without it a screen reader is handed rows
  // belonging to nothing: axe reports aria-required-parent on every row in every roster,
  // and the sortable column headers announce as bare buttons rather than as the column
  // headers of a table. Grid rather than table because the headers are activatable.
  const panel = el("div", {
    id: panelId,
    class: "spr-roster",
    role: "grid",
    // Named by the agenda item it belongs to. A page of rosters that all announce as
    // "grid" gives a screen reader reader no way to tell which item they are in.
    "aria-label": `Speakers for ${item.title || "this agenda item"}`,
    "data-testid": "speaker-report-roster",
    "data-registered-count": item.registeredCount,
    // The column COUNT is published so the stylesheet can pin a fixed template per width
    // instead of sharing the generic `repeat(auto-fit, ...)` .spr-row. auto-fit collapses a
    // track whose cell is empty, so one item with no phone callers -- or, now, one item
    // where nobody's timer was run -- would silently render with different columns from the
    // item above it. .spr-grid-agenda, .spr-grid-recon and .spr-grid-directory all pin their
    // own templates for exactly this reason; the roster was the one grid still sharing.
    "data-columns": columns.length,
    hidden: true
  });

  const draw = (): void => {
    const rows = sorted(item.signUps, columns, sort);
    const shown = rows.slice(0, options.renderCap);
    const omitted = rows.length - shown.length;

    const children: Array<Node | null> = [headerRow(columns, sort, applySort)];

    // Grouped by channel, in-person first. A band rather than a column, so the
    // reader sees the split without sorting for it.
    for (const channel of ["InPerson", "Phone"] as const) {
      const group = shown.filter((s) => s.channel === channel);
      if (group.length === 0) continue;

      children.push(el("div", {
        class: "spr-group-band",
        "data-channel": channelKey(channel),
        role: "presentation"
      }, [
        el("span", { class: "spr-group-name", text: channelLabel(channel).toUpperCase() }),
        el("span", { class: "spr-group-count", text: String(group.length) })
      ]));

      for (const signUp of group) {
        children.push(dataRow(signUp, columns, options));
      }
    }

    if (shown.length === 0) {
      children.push(el("div", {
        class: "spr-roster-empty",
        text: "No speakers registered for this item."
      }));
    }

    if (omitted > 0) {
      // Stated, never silent. The prototype capped rosters at 34 rows with no UI at
      // all, so a contentious item simply lost its tail.
      children.push(el("p", {
        class: "spr-omitted",
        "data-testid": "speaker-report-roster-omitted",
        text: `${omitted} more registered speakers are not shown here. Export the report to see every row.`
      }));
    }

    replaceChildrenWith(panel, children);
  };

  function applySort(column: RosterColumn): void {
    if (sort.column === column) {
      sort.direction = sort.direction === "ascending" ? "descending" : "ascending";
    } else {
      sort.column = column;
      sort.direction = "ascending";
    }
    draw();

    // Re-drawing replaced the button that was clicked, so focus has to be put back
    // or a keyboard user is dumped at the top of the document mid-task.
    const header = panel.querySelector<HTMLElement>(`[data-roster-sort="${column}"]`);
    header?.focus();
  }

  toggle.addEventListener("click", () => {
    const open = toggle.getAttribute("aria-expanded") === "true";
    toggle.setAttribute("aria-expanded", open ? "false" : "true");
    panel.hidden = open;
    if (!open && panel.childElementCount === 0) draw();
  });

  draw();
  panel.hidden = true;

  return { toggle, panel };
}

function headerRow(
  columns: ColumnSpec[],
  sort: SortState,
  onSort: (column: RosterColumn) => void
): HTMLElement {
  const row = el("div", { class: "spr-row spr-row-head", role: "row" });

  for (const column of columns) {
    const cell = el("div", {
      class: "spr-cell spr-cell-head",
      role: "columnheader",
      "data-column-key": column.key
    });

    if (!column.sortable) {
      cell.appendChild(el("span", { text: column.label }));
      row.appendChild(cell);
      continue;
    }

    const active = sort.column === column.key;
    const direction = active ? sort.direction : "ascending";

    // The direction goes in the ACCESSIBLE NAME, not in aria-sort: aria-sort is only
    // valid on a columnheader inside a table, and this is a grid.
    const button = el("button", {
      type: "button",
      class: "spr-sort" + (active ? " is-active" : ""),
      "data-roster-sort": column.key,
      "data-sort-direction": direction,
      "aria-label": active
        ? `${column.label}, sorted ${direction}. Activate to sort ${direction === "ascending" ? "descending" : "ascending"}.`
        : `${column.label}, not sorted. Activate to sort ascending.`
    }, [
      el("span", { text: column.label }),
      el("span", { class: "spr-sort-mark", "aria-hidden": "true", text: active ? (direction === "ascending" ? "↑" : "↓") : "" })
    ]);

    button.addEventListener("click", () => onSort(column.key));
    cell.appendChild(button);
    row.appendChild(cell);
  }

  return row;
}

function dataRow(signUp: SignUp, columns: ColumnSpec[], options: RosterOptions): HTMLElement {
  const row = el("div", {
    class: "spr-row",
    role: "row",
    "data-testid": "speaker-report-session-row",
    "data-session-id": signUp.signUpId,
    "data-speaker-name": signUp.speakerName,
    "data-channel": channelKey(signUp.channel),
    "data-outcome": outcomeKey(signUp.outcome),
    "data-turn-state": signUp.turnState.toLowerCase(),
    "data-over-cap": signUp.wasOverCap ? "true" : "false"
  });

  for (const column of columns) {
    const cell = cellFor(column.key, signUp, options);

    // Under the stacked breakpoint the header row is hidden, so each cell carries its own
    // label. A REAL element rather than a ::before, and aria-hidden, because the cell is
    // already named by its columnheader: a generated-content label is announced by some
    // screen readers and not others, and where it is announced the reader hears the column
    // name twice. Hidden at desktop width by the stylesheet, not by markup, so one rule
    // governs when it appears rather than two that can drift.
    cell.insertBefore(
      el("span", { class: "spr-cell-label", "aria-hidden": "true", text: column.label }),
      cell.firstChild);

    row.appendChild(cell);
  }

  return row;
}

function cellFor(column: RosterColumn, signUp: SignUp, options: RosterOptions): HTMLElement {
  const cell = el("div", { class: "spr-cell", role: "gridcell", "data-column-key": column });

  switch (column) {
    case "speaker": {
      const name = el("button", {
        type: "button",
        class: "spr-name",
        "data-speaker-user-id": signUp.userId,
        text: signUp.speakerName
      });
      name.addEventListener("click", () => options.onSpeakerActivated(signUp.userId, name));
      cell.appendChild(name);

      if (signUp.partyLineSpokeCount !== null) {
        // One row, several humans. Shown on the row because the totals cannot express
        // it: this is one sign-up and one directory entry however many people spoke.
        cell.appendChild(el("span", {
          class: "spr-chip spr-chip-party",
          "data-party-line-spoke-count": signUp.partyLineSpokeCount,
          text: `${signUp.partyLineSpokeCount} spoke from this line`
        }));
      }

      // Organization is NOT repeated here: it has its own column when the data exists.
      return cell;
    }

    case "organization":
      if (signUp.organization) {
        cell.appendChild(el("span", { class: "spr-org", text: signUp.organization }));
      } else {
        // Stated, not blank: a dash whose mouseover and screen-reader text say "None given".
        // An empty cell reads as a rendering fault; this reads as a speaker who named none.
        for (const span of blankSpans("None given")) cell.appendChild(span);
      }
      return cell;

    case "channel":
      cell.appendChild(el("span", {
        class: `spr-chip spr-chip-channel spr-channel-${channelKey(signUp.channel)}`,
        text: channelLabel(signUp.channel)
      }));
      return cell;

    case "allotment": {
      if (signUp.speakingSeconds === null || !signUp.allottedSeconds) {
        cell.appendChild(el("span", { class: "spr-untimed", text: "—" }));
        return cell;
      }
      const used = Math.round((signUp.speakingSeconds / signUp.allottedSeconds) * 100);
      cell.setAttribute("data-percent-of-allotment", String(used));
      cell.appendChild(el("span", { class: "spr-len", text: `${used}%` }));
      return cell;
    }

    case "position":
      cell.appendChild(el("span", {
        class: `spr-pos spr-pos-${positionKey(signUp.position)}`,
        text: positionLabel(signUp.position)
      }));
      return cell;

    case "outcome":
      cell.appendChild(el("span", {
        class: `spr-outcome spr-outcome-${outcomeKey(signUp.outcome)}`,
        text: outcomeLabel(signUp.outcome)
      }));

      if (signUp.wasOverCap) {
        // A qualifier on the outcome, never an outcome of its own, and worded as a
        // fact about the meeting rather than as an accusation about the speaker.
        cell.appendChild(el("span", {
          class: "spr-chip spr-chip-cap",
          text: "over the speaker cap"
        }));
      }
      return cell;

    case "started": {
      // On the MEETING'S clock, never the reader's -- `turnStamp` takes the offset the
      // server resolved. The heading says so once, and says the opposite once, when the
      // server could not resolve the zone and these fell back to the browser's.
      const startMs = firstStartMs(sessionsForSignUp(options.turns, signUp));
      cell.appendChild(el("span", {
        class: startMs === null ? "spr-untimed" : "spr-len spr-num",
        "data-testid": "session-started",
        "data-started-utc": startMs === null ? null : new Date(startMs).toISOString(),
        text: turnStamp(startMs, options.turns.clock)
      }));
      return cell;
    }

    case "spokeFor": {
      const sessions = sessionsForSignUp(options.turns, signUp);
      const { seconds, excluded } = spokenSeconds(sessions);

      cell.appendChild(el("span", {
        class: seconds === null ? "spr-untimed" : "spr-len spr-num",
        "data-testid": "session-spoke-for",
        "data-spoken-seconds": seconds === null ? null : seconds,
        text: decimalMinutes(seconds)
      }));

      // A speaker can hold the microphone more than once on one item. The figure is the
      // SUM, so a row that is a sum of several turns says how many -- otherwise a clerk
      // comparing it against a single turn in the transcript reads a discrepancy.
      if (sessions.length > 1) {
        cell.appendChild(el("span", {
          class: "spr-bar-note",
          text: `${sessions.length} turns`
        }));
      }

      // Stated on the cell, not folded into the number. An open turn contributes nothing to
      // the sum and the reader has to be told, or the total silently under-reports.
      if (excluded > 0) {
        cell.appendChild(el("span", {
          class: "spr-chip spr-chip-open",
          text: excluded === 1
            ? "1 turn not recorded"
            : `${excluded} turns not recorded`
        }));
      }
      return cell;
    }

    case "length": {
      if (signUp.speakingSeconds === null || signUp.speakingSeconds === undefined) {
        // The dash carries the data-testid so the column's tests still address one node.
        const [dash, note] = untimedSpans();
        dash.setAttribute("data-testid", "session-length");
        note.setAttribute("data-testid", "session-length-note");
        cell.appendChild(dash);
        cell.appendChild(note);
      } else {
        cell.appendChild(el("span", {
          class: "spr-len",
          "data-testid": "session-length",
          text: duration(signUp.speakingSeconds)
        }));
      }

      if (signUp.turnState === "Open") {
        cell.appendChild(el("span", {
          class: "spr-chip spr-chip-open",
          text: "timer left running"
        }));
      }
      return cell;
    }

    case "language": {
      const name = languageName(signUp.languageCode);
      if (!name) {
        for (const span of blankSpans("None given")) cell.appendChild(span);
        return cell;
      }
      // The raw code stays on the node for a test or a support ticket; the reader sees one
      // name whichever system the code came from.
      cell.appendChild(el("span", {
        class: "spr-lang",
        "data-language-code": signUp.languageCode,
        text: name
      }));
      return cell;
    }

    default:
      return cell;
  }
}

/**
 * Sorts a copy. Nulls always sort last, in both directions, because "not timed" is
 * an absence rather than a value -- pushing it to the top on a descending sort would
 * bury the longest speech under the rows nobody measured.
 */
function sorted(rows: SignUp[], columns: ColumnSpec[], sort: SortState): SignUp[] {
  if (sort.column === null) return rows.slice();

  const spec = columns.filter((c) => c.key === sort.column)[0];
  if (!spec) return rows.slice();

  const flip = sort.direction === "ascending" ? 1 : -1;

  return rows.slice().sort((a, b) => {
    const left = spec.value(a);
    const right = spec.value(b);

    if (left === null && right === null) return tieBreak(a, b) * flip;
    if (left === null) return 1;
    if (right === null) return -1;

    if (left < right) return -1 * flip;
    if (left > right) return 1 * flip;
    return tieBreak(a, b) * flip;
  });
}

/** Name, then sign-up id, so a re-sort is deterministic across reads. */
function tieBreak(a: SignUp, b: SignUp): number {
  const byName = a.speakerName.toLowerCase().localeCompare(b.speakerName.toLowerCase());
  if (byName !== 0) return byName;
  return a.signUpId.localeCompare(b.signUpId);
}
